import Phaser from "phaser";

import { api } from "../api";
import { gestureById, presetFor } from "../avatar/catalog";
import { avatarPartsReady } from "../avatar/composer";
import { avatarTextureKey } from "../avatar/textures";
import { DEFAULT_ZOOM, DEPTH, loadZoom, MINIGAME_SCENES, RANKING_TABS, saveZoom, ZOOM_MAX, ZOOM_MIN } from "../config";
import { Avatar, FONT, textResolution } from "../entities/Avatar";
import { FoxyNPC } from "../entities/FoxyNPC";
import type { SocketClient } from "../net/SocketClient";
import { DmPanel } from "../ui/DmPanel";
import { showDossier, type DossierResult } from "../ui/DossierView";
import type { Hud } from "../ui/Hud";
import { showInfo } from "../ui/InfoOverlay";
import { showLeaderboard } from "../ui/LeaderboardOverlay";
import { showMinigameMenu } from "../ui/MinigameMenu";
import { showAvatarCustomizer } from "../ui/AvatarCustomizer";
import { showPlayersPanel } from "../ui/PlayersPanel";
import { showProfileMenu } from "../ui/ProfileMenu";
import type { TouchControls } from "../ui/TouchControls";
import { OPEN_NOT_PRIVATE, type VoiceAction } from "../ui/VoicePanel";
import { VoiceChat, type VoicePeer } from "../voice/VoiceChat";
import {
  BANNER_CODES,
  KICK_CODES,
  KIND_SEAT,
  SESSION_REPLACED,
  type Interactable,
  type KickCode,
  type PlayerInfo,
  type VoiceInfo,
  type VoiceKind,
  type WelcomeMessage,
} from "../types";
import type { Session } from "../session";

/** Dados injetados pelo main.ts atraves do registry do jogo. */
export interface GardenContext {
  socket: SocketClient;
  welcome: WelcomeMessage;
  session: Session;
  hud: Hud;
  touch: TouchControls;
  onLeave: () => void;
  /** Avatar ou nome publico mudaram: main.ts persiste e reconecta ao mundo. */
  onProfileUpdated: (session: Session) => void;
  /** O admin derrubou a conexao (conta desativada, sessoes encerradas, cadastro editado). */
  onKicked: (message: string, code: KickCode) => void;
}

// Caixa de colisao do jogador. Precisa ser identica a do servidor
// (server/internal/world/tilemap.go), senao a predicao briga com a correcao.
const BODY_HALF_W = 10;
const BODY_HALF_H = 6;
const BODY_OFFSET = -6;

/** Acima disso, corrigimos a posicao com um salto em vez de suavizar. */
const SNAP_DISTANCE = 80;
/** Fracao do erro corrigida por snapshot enquanto o jogador anda. */
const WALK_CORRECTION = 0.25;
/** Constante de tempo (ms) com que o desenho alcanca a posicao corrigida. */
const RENDER_SMOOTH_MS = 80;
/** Quanto historico da predicao guardamos para comparar com o servidor. */
const PRED_HISTORY_MS = 1000;
/** Intervalo maximo entre envios de input, mesmo sem mudanca. */
const INPUT_RESEND_MS = 120;
/**
 * Teto de envios por segundo. O analogico muda de valor a cada quadro; sem
 * isso o cliente inundaria o servidor com 60 mensagens por segundo.
 */
const INPUT_MIN_INTERVAL_MS = 50;
/** Variacao no eixo que justifica um envio antes da hora. */
const INPUT_EPSILON = 0.08;
/** Opacidade da cobertura (ex.: quiosque, pergolado) com o jogador embaixo dela. */
const ROOF_FADED_ALPHA = 0.3;
/** De quanto em quanto tempo o volume da voz acompanha as distancias. */
const VOICE_PROXIMITY_MS = 150;

/** Objeto da camada "props" do garden.json (gerada por tools/genassets). */
interface TiledPropObject {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  properties?: Array<{ name: string; value: number }>;
}

export class GardenScene extends Phaser.Scene {
  private ctx!: GardenContext;
  private socket!: SocketClient;

  private collision: number[] = [];
  private mapWidth = 0;
  private mapHeight = 0;
  private tileSize = 32;
  private walkSpeed = 176;

  private avatars = new Map<string, Avatar>();
  private me: Avatar | null = null;
  private myId = "";

  /** Posicao prevista localmente do avatar do jogador. */
  private predX = 0;
  private predY = 0;
  /** Predicoes recentes, para comparar com snapshots que chegam atrasados. */
  private predHistory: { t: number; x: number; y: number }[] = [];
  /**
   * Diferenca entre o que esta desenhado e a predicao. As correcoes mudam a
   * predicao na hora, mas o desenho a alcanca aos poucos, sem salto.
   */
  private renderOffX = 0;
  private renderOffY = 0;
  private tickMs = 50;

  private inputSeq = 0;
  /**
   * Seq do ultimo input que MUDOU o movimento (andar, virar, parar, levantar).
   * Os reenvios periodicos do mesmo input nao mexem em nada no servidor, entao
   * basta este ter sido confirmado para o snapshot ja refletir o que o jogador
   * pediu. Comparar com `inputSeq` nunca fechava com ping acima do intervalo
   * de reenvio: sempre havia um reenvio mais novo a caminho.
   */
  private motionSeq = 0;
  private lastDX = 0;
  private lastDY = 0;
  private lastInputSentAt = 0;

  private keys!: {
    up: Phaser.Input.Keyboard.Key[];
    down: Phaser.Input.Keyboard.Key[];
    left: Phaser.Input.Keyboard.Key[];
    right: Phaser.Input.Keyboard.Key[];
  };

  /** Coberturas que ficam transparentes com o jogador dentro ou atras delas. */
  private fadingRoofs: Array<{ image: Phaser.GameObjects.Image; area: Phaser.Geom.Rectangle }> = [];

  private interactables: Interactable[] = [];
  private nearby: Interactable | null = null;
  /** A Foxy NPC (nasce no primeiro snapshot que a traz) e o alcance do carinho. */
  private foxy: FoxyNPC | null = null;
  private foxyRadius = 0;
  private foxyLabel = "";
  private nearFoxy = false;
  /** Assento do jogador local (0 = de pe), confirmado pelo servidor. */
  private mySeat = 0;
  private overlayOpen = false;
  private zoom = 3;
  /** Zoom escolhido nos botoes do HUD; null segue o padrao (DEFAULT_ZOOM). */
  private zoomChoice: number | null = loadZoom();
  /** true depois que o servidor encerrou a sessao de proposito. */
  private sessionEnded = false;
  /** Chat de voz (null = servidor sem LiveKit). */
  private voice: VoiceChat | null = null;
  /** Mensagens privadas (so quem tem conta). */
  private dm: DmPanel | null = null;
  private voiceInfo: VoiceInfo | null = null;
  private voiceKind: VoiceKind = "open";
  /** Conversa trancada do jogador local ("" = nenhuma), vinda do snapshot. */
  private myLocked = "";
  private voiceUpdatedAt = 0;
  private voiceZoneLayer: Phaser.GameObjects.Graphics | null = null;
  private voiceZoneLabels: Phaser.GameObjects.Text[] = [];
  /** O aviso de que a area aberta nao e privada ja apareceu no chat. */
  private openWarned = false;
  private unsubscribes: Array<() => void> = [];

  constructor() {
    super("garden");
  }

  create(): void {
    this.ctx = this.registry.get("context") as GardenContext;
    this.socket = this.ctx.socket;

    const { welcome } = this.ctx;
    this.walkSpeed = welcome.walkSpeed;
    this.tickMs = 1000 / welcome.tickRate;
    this.interactables = welcome.interactables ?? [];
    this.foxyRadius = welcome.foxy?.radius ?? 0;
    this.foxyLabel = welcome.foxy?.label ?? "";

    this.buildMap();
    this.setupCamera();
    this.setupLocalActor();
    this.setupInput();
    this.setupSocket();
    this.setupHud();
    this.setupVoice();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    // game.destroy() (sair, trocar de conta) nao passa pelo SHUTDOWN: sem isto
    // a conexao de voz ficaria aberta depois de sair do Jardim
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
    // Inspecao no console do navegador durante o desenvolvimento.
    if (import.meta.env.DEV) (window as unknown as { __garden?: GardenScene }).__garden = this;
  }

  // ------------------------------------------------------------------ mapa

  private buildMap(): void {
    const map = this.make.tilemap({ key: "garden" });
    const tileset = map.addTilesetImage("garden", "tileset-garden");
    if (!tileset) throw new Error("tileset 'garden' não encontrado no mapa");

    map.createLayer("ground", tileset, 0, 0)?.setDepth(DEPTH.ground);
    map.createLayer("decor", tileset, 0, 0)?.setDepth(DEPTH.decor);
    map.createLayer("overhead", tileset, 0, 0)?.setDepth(DEPTH.overhead);

    // A camada de colisao nao e desenhada: os dados crus alimentam a predicao
    // local, com a mesma convencao do servidor (0 = livre, != 0 = bloqueado).
    const raw = this.cache.tilemap.get("garden")?.data as
      | {
          width: number;
          height: number;
          tilewidth: number;
          layers: Array<{ name: string; data?: number[]; objects?: TiledPropObject[] }>;
        }
      | undefined;

    if (!raw) throw new Error("mapa 'garden' ausente no cache");

    this.mapWidth = raw.width;
    this.mapHeight = raw.height;
    this.tileSize = raw.tilewidth;
    this.collision = raw.layers.find((layer) => layer.name === "collision")?.data ?? [];
    this.buildProps(raw.layers.find((layer) => layer.name === "props")?.objects ?? []);

    if (this.collision.length !== this.mapWidth * this.mapHeight) {
      console.warn("camada de colisão ausente ou com tamanho inesperado; a predição vai ficar imprecisa");
      this.collision = [];
    }
  }

  /**
   * Pecas altas (arvores, lampioes, estatua da Foxy, pilares do quiosque) sao
   * sprites com depth = actors + baseY, o mesmo y-sorting dos avatares: quem
   * esta com o pe acima da base fica atras, quem esta abaixo fica na frente.
   * Coberturas ("overhead") ficam acima de todos e somem com o jogador embaixo.
   */
  private buildProps(objects: TiledPropObject[]): void {
    for (const o of objects) {
      if (!this.textures.get("props").has(o.name)) {
        console.warn("prop sem frame no atlas:", o.name);
        continue;
      }
      const prop = (name: string) => o.properties?.find((p) => p.name === name)?.value;
      const image = this.add.image(o.x, o.y, "props", o.name).setOrigin(0, 0);
      if (prop("overhead")) {
        image.setDepth(DEPTH.overhead);
        const fadeW = prop("fadeW");
        const fadeH = prop("fadeH");
        if (fadeW && fadeH) {
          const area = new Phaser.Geom.Rectangle(prop("fadeX") ?? o.x, prop("fadeY") ?? o.y, fadeW, fadeH);
          this.fadingRoofs.push({ image, area });
        }
      } else {
        image.setDepth(DEPTH.actors + (prop("baseY") ?? o.y + o.height));
      }
    }
  }

  /** Esmaece as coberturas quando o jogador local esta dentro ou atras delas. */
  private updateRoofs(delta: number): void {
    const step = Math.min(1, delta / 120);
    for (const { image, area } of this.fadingRoofs) {
      const target = this.me && area.contains(this.predX, this.predY) ? ROOF_FADED_ALPHA : 1;
      image.setAlpha(image.alpha + (target - image.alpha) * step);
    }
  }

  private setupCamera(): void {
    const { map } = this.ctx.welcome;
    this.cameras.main.setBounds(0, 0, map.pixelWidth, map.pixelHeight);
    this.cameras.main.roundPixels = true;
    this.cameras.main.setBackgroundColor("#16201b");

    this.zoom = this.zoomChoice ?? DEFAULT_ZOOM;
    this.cameras.main.setZoom(this.zoom);
  }

  /** Botoes do HUD e teclas +/-: um passo inteiro, lembrado entre sessoes. */
  private stepZoom(step: number): void {
    const zoom = Phaser.Math.Clamp(this.zoom + step, ZOOM_MIN, ZOOM_MAX);
    if (zoom === this.zoom) return;
    this.zoomChoice = zoom;
    saveZoom(zoom);
    this.applyZoom();
  }

  /** Aplica o zoom escolhido nos botoes. */
  private applyZoom(): void {
    const zoom = this.zoomChoice ?? DEFAULT_ZOOM;
    if (zoom === this.zoom) return;

    this.zoom = zoom;
    this.cameras.main.setZoom(zoom);
    this.me?.applyZoom(zoom);
    for (const avatar of this.avatars.values()) avatar.applyZoom(zoom);
    this.foxy?.applyZoom(zoom);
    for (const label of this.voiceZoneLabels) label.setResolution(textResolution(zoom));
    this.ctx.hud.setZoom(zoom, ZOOM_MIN, ZOOM_MAX);
  }

  private setupLocalActor(): void {
    const { welcome, session } = this.ctx;

    const you: PlayerInfo = welcome.you ?? {
      id: session.user.id,
      name: session.user.displayName,
      skin: session.user.skin,
      x: welcome.map.spawnX,
      y: welcome.map.spawnY,
    };
    this.myId = you.id;
    this.predX = you.x;
    this.predY = you.y;
    this.me = new Avatar(this, you, true, avatarTextureKey(this, you));

    // segue o pe no chao: a camera nao quica junto com o pulo
    this.cameras.main.startFollow(this.me.anchor, true, 0.15, 0.15);
  }

  private setupInput(): void {
    const keyboard = this.input.keyboard;
    if (!keyboard) throw new Error("teclado indisponível");

    // Clique (sem arrastar) num avatar abre o dossie; o cursor vira maozinha
    // em cima de quem tem dossie. No toque o arraste cobre o canvas: la e o
    // TouchControls que avisa do toque curto (onTap).
    this.input.on(Phaser.Input.Events.POINTER_UP, (pointer: Phaser.Input.Pointer) => {
      if (pointer.getDistance() > 6 || pointer.getDuration() > 400) return;
      this.dossierAt(pointer.x, pointer.y);
    });
    this.input.on(Phaser.Input.Events.POINTER_MOVE, (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) return;
      this.input.setDefaultCursor(this.avatarAt(pointer.x, pointer.y) ? "pointer" : "default");
    });

    // O proprio Phaser cancela o comportamento padrao destas teclas (rolar a
    // pagina). Nao faca isso com um listener de window: o Phaser descarta
    // eventos que ja chegam com defaultPrevented, e o jogo para de responder.
    keyboard.addCapture(["UP", "DOWN", "LEFT", "RIGHT", "SPACE"]);

    const key = (code: string) => keyboard.addKey(code, false);
    this.keys = {
      up: [key("W"), key("UP")],
      down: [key("S"), key("DOWN")],
      left: [key("A"), key("LEFT")],
      right: [key("D"), key("RIGHT")],
    };

    keyboard.on("keydown-E", () => this.tryInteract());
    keyboard.on("keydown-SPACE", () => this.tryInteract());

    // G abre o seletor de gestos; 1..9 fazem o gesto direto; Esc fecha
    const gestures = this.ctx.hud.gestures;
    keyboard.on("keydown-G", () => {
      if (!this.overlayOpen && !this.ctx.hud.chatFocused) gestures.toggle();
    });
    keyboard.on("keydown-ESC", () => gestures.close());
    // M liga e desliga o microfone do chat de voz
    keyboard.on("keydown-M", () => {
      if (!this.overlayOpen && !this.ctx.hud.chatFocused) this.voice?.toggleMic();
    });

    // +/- (e o teclado numerico) aproximam e afastam
    keyboard.on("keydown", (event: KeyboardEvent) => {
      if (this.overlayOpen || this.ctx.hud.chatFocused || event.ctrlKey || event.altKey || event.metaKey) return;
      if (event.key === "+" || event.key === "=") this.stepZoom(1);
      else if (event.key === "-" || event.key === "_") this.stepZoom(-1);
    });
    keyboard.on("keydown", (event: KeyboardEvent) => {
      if (!/^[1-9]$/.test(event.key) || event.ctrlKey || event.altKey || event.metaKey) return;
      const id = gestures.idForKey(Number(event.key));
      if (id) {
        gestures.close();
        this.selectGesture(id);
      }
    });
  }

  private setupHud(): void {
    const { hud, welcome, session, touch } = this.ctx;

    hud.setOnline(welcome.online);
    hud.setProfile(session.user);
    hud.show();

    hud.onChat((text) => this.socket.sendChat(text));
    hud.onProfileClick(() => this.openProfile());
    hud.setPlayersAvailable(true);
    hud.onPlayersClick(() => this.openPlayers());
    hud.setDmAvailable(true);
    hud.setDmUnread(0);
    const dm = new DmPanel(session);
    dm.onUnreadChange((count) => hud.setDmUnread(count));
    dm.onViewDossier((id) => this.openDossier(id));
    // No toque as mensagens cobrem a tela: o avatar para e os controles saem.
    dm.onOpenChange((open) => {
      if (!touch.enabled || this.overlayOpen) return;
      if (open) this.sendInput(0, 0, true);
      touch.setMode(open ? "hidden" : "world");
    });
    hud.onDmClick(() => {
      if (!this.overlayOpen) dm.toggle();
    });
    this.dm = dm;
    hud.onZoom((step) => this.stepZoom(step));
    hud.setZoom(this.zoom, ZOOM_MIN, ZOOM_MAX);
    hud.onChatFocusChange((focused) => {
      // Enquanto o jogador digita, o avatar para de andar.
      if (focused) this.sendInput(0, 0, true);
    });

    touch.onInteract(() => this.tryInteract());
    // Dossie: nome no chat, toque curto num avatar (no mouse, ver setupInput)
    hud.onNameClick((id) => this.openDossier(id));
    touch.onTap((x, y) => this.dossierAt(this.scale.transformX(x), this.scale.transformY(y)));
    touch.onChat(() => hud.focusChat());
    touch.onGesture(() => {
      if (!this.overlayOpen) hud.gestures.toggle();
    });
    hud.gestures.onSelect((id) => this.selectGesture(id));
    hud.gestures.canOpen = () => !this.overlayOpen;
    hud.canChat = () => !this.overlayOpen; // Enter abre o chat (ver Hud)
    touch.setMode("world");

    const controles = touch.enabled
      ? "Arraste o dedo em qualquer ponto da tela para andar."
      : "Use WASD ou as setas para andar, E para interagir e G para os gestos.";
    hud.addSystem(`Boas-vindas ao Jardim, ${welcome.you?.name ?? session.user.displayName}! ${controles}`);
    if (!session.user.emailVerified) {
      hud.addSystem(
        "Confirme seu e-mail pelo link que enviamos para recuperar a conta se esquecer a senha. " +
          "É opcional — dá para reenviar o link no seu dossiê.",
        "info",
      );
    }
    // Conta que ainda nao montou o avatar (inclui todas as anteriores ao
    // customizador): o editor abre logo na entrada.
    if (!session.user.appearance) this.openCustomizer(session, true);
  }

  private setupSocket(): void {
    const { hud } = this.ctx;

    this.unsubscribes.push(
      this.socket.on("state", (msg) => {
        for (const info of msg.joined ?? []) this.spawnAvatar(info);
        for (const id of msg.left ?? []) this.removeAvatar(id);

        for (const state of msg.players) {
          if (state.id === this.myId) {
            // Com movimento ainda nao confirmado (ex.: andou para levantar), o
            // snapshot pode ser anterior a ele: vale o assento previsto aqui.
            if ((msg.ack ?? 0) >= this.motionSeq) this.setMySeat(state.s ?? 0);
            this.reconcile(state.x, state.y, msg.ack ?? 0);
            this.me?.setAura(state.a);
            this.me?.setCharge(msg.charge ?? 0);
            this.myLocked = state.v ?? "";
            this.me?.setLocked(state.v);
            continue;
          }
          const avatar = this.avatars.get(state.id);
          avatar?.applyServerState(state);
          avatar?.setLocked(state.v);
        }
        if (msg.foxy) {
          if (this.foxy) this.foxy.apply(msg.foxy);
          else this.foxy = new FoxyNPC(this, msg.foxy);
        }
        hud.setOnline(msg.online);
        hud.setLatency(this.socket.latency);
      }),

      this.socket.on("dm", (msg) => this.dm?.receive(msg.message)),

      this.socket.on("foxy", (msg) => {
        this.foxy?.pet();
        if (msg.notice) {
          const who = msg.id === this.myId ? "Você" : msg.name;
          hud.addSystem(`${who} fez carinho na Foxy! ♥`, "info");
        }
      }),

      this.socket.on("chat", (msg) => {
        hud.addChat(msg.name, msg.text, msg.id === this.myId, msg.id);
        const speaker = msg.id === this.myId ? this.me : this.avatars.get(msg.id);
        speaker?.say(msg.text);
      }),

      this.socket.on("system", (msg) => {
        hud.addSystem(msg.message, msg.level ?? "info");
        // anuncio do admin: fica na tela ate a pessoa fechar
        if (msg.code && BANNER_CODES.includes(msg.code)) hud.showBanner(msg.message, msg.level ?? "warn", true);
        if (msg.code === SESSION_REPLACED) {
          // Reconectar aqui faria esta aba e a nova se derrubarem sem parar.
          this.socket.stopReconnecting();
          this.sessionEnded = true;
        }
        if (KICK_CODES.includes(msg.code as KickCode)) {
          // O servidor ja vai fechar a conexao: quem decide o proximo passo
          // (tela de entrada ou recarregar o perfil) e o main.ts.
          this.socket.stopReconnecting();
          this.sessionEnded = true;
          this.ctx.onKicked(msg.message, msg.code as KickCode);
        }
      }),

      this.socket.on("interact", (msg) => {
        if (msg.kind === "minigame" && msg.game) {
          this.openMinigame(msg.game, msg.label ?? msg.game);
        } else if (msg.kind === "leaderboard") {
          this.openLeaderboard(msg.game || RANKING_TABS[0].slug, msg.label ?? "upRanking", !msg.game);
        } else if (msg.kind === "info") {
          this.openInfo(msg.label ?? "", msg.text ?? "");
        }
      }),

      this.socket.on("welcome", (msg) => {
        // Reconexao: o servidor reenvia o welcome, entao limpamos os avatares
        // antigos e voltamos para a posicao que ele considera valida.
        for (const avatar of this.avatars.values()) avatar.destroy();
        this.avatars.clear();
        this.foxyRadius = msg.foxy?.radius ?? 0;
        this.foxyLabel = msg.foxy?.label ?? "";
        if (msg.you) {
          this.myId = msg.you.id;
          this.myLocked = "";
          this.me?.setLocked(undefined);
          this.setMySeat(0);
          this.setMyGesture(undefined);
          this.predX = msg.you.x;
          this.predY = msg.you.y;
          this.resetPrediction();
          this.me?.setPosition(msg.you.x, msg.you.y);
        }
        // as mensagens privadas que chegaram com a conexao caida
        this.dm?.resync();
        hud.addSystem("Reconectado ao mundo.", "info");
      }),

      this.socket.onConnection("close", (reason) => {
        if (this.sessionEnded) {
          this.ctx.touch.setMode("hidden");
          hud.setHint(null);
          return;
        }
        hud.addSystem(reason, "error");
      }),
      this.socket.onConnection("reconnecting", (attempt) =>
        hud.addSystem(`Tentando reconectar (tentativa ${attempt})…`, "warn"),
      ),
    );
  }

  private spawnAvatar(info: PlayerInfo): void {
    if (info.id === this.myId || this.avatars.has(info.id)) return;
    this.avatars.set(info.id, new Avatar(this, info, false, avatarTextureKey(this, info)));
  }

  private removeAvatar(id: string): void {
    this.avatars.get(id)?.destroy();
    this.avatars.delete(id);
  }

  // ---------------------------------------------------------------- update

  override update(_time: number, delta: number): void {
    const dt = delta / 1000;

    const [dx, dy] = this.readInput();
    this.moveLocal(dx, dy, dt);

    for (const avatar of this.avatars.values()) avatar.update(delta);
    this.foxy?.update(delta);
    this.me?.update(delta);

    this.updateRoofs(delta);
    this.updateInteractionHint();
    this.updateVoice();
  }

  /**
   * Direcao desejada. O analogico tem prioridade sobre o teclado e devolve
   * valores fracionarios: quanto mais perto do centro, mais devagar o avatar
   * anda. O teclado continua binario (-1, 0 ou 1).
   */
  private readInput(): [number, number] {
    if (this.ctx.hud.chatFocused || this.overlayOpen) return [0, 0];

    const stick = this.ctx.touch.axis;
    if (stick.x !== 0 || stick.y !== 0) return [stick.x, stick.y];

    const down = (keys: Phaser.Input.Keyboard.Key[]) => keys.some((k) => k.isDown);
    const dx = (down(this.keys.right) ? 1 : 0) - (down(this.keys.left) ? 1 : 0);
    const dy = (down(this.keys.down) ? 1 : 0) - (down(this.keys.up) ? 1 : 0);
    return [dx, dy];
  }

  /**
   * Converte a direcao pedida em velocidade, com a mesma regra do servidor:
   * magnitude ate 1 passa direto (analogico anda mais devagar), acima de 1 e
   * normalizada (a diagonal do teclado nao pode ser mais rapida).
   */
  private velocityFrom(dx: number, dy: number): [number, number] {
    const magnitude = Math.hypot(dx, dy);
    if (magnitude < 0.001) return [0, 0];
    const scale = Math.min(magnitude, 1) / magnitude;
    return [dx * scale, dy * scale];
  }

  /** Predicao do movimento local (input para o servidor, reconciliacao). */
  private moveLocal(dx: number, dy: number, dt: number): void {
    // Parar precisa chegar ao servidor na hora, senao o avatar segue andando.
    // Sentado, o primeiro passo tambem vai na hora: e ele que levanta.
    const stopping = dx === 0 && dy === 0 && (this.lastDX !== 0 || this.lastDY !== 0);
    const standing = this.mySeat !== 0 && (dx !== 0 || dy !== 0);
    this.sendInput(dx, dy, stopping || standing);
    if (!this.me) return;

    // Mesma regra do servidor: andar levanta e o passo comeca na frente do
    // banco (o assento fica dentro da colisao, dali nao daria para sair).
    if (standing) this.standUpLocally();

    const [vx, vy] = this.velocityFrom(dx, dy);
    if (vx !== 0 || vy !== 0) {
      const step = this.walkSpeed * dt;
      const [nx, ny] = this.slide(this.predX, this.predY, vx * step, vy * step);
      this.predX = nx;
      this.predY = ny;
      this.me.setFacing(directionOf(dx, dy), true);
      // mesma regra do servidor: andar encerra o gesto
      if (this.me.gesture) this.setMyGesture(undefined);
    } else {
      this.me.setFacing(currentDirection(this.me), false);
    }

    const now = this.time.now;
    this.predHistory.push({ t: now, x: this.predX, y: this.predY });
    while (this.predHistory.length > 2 && this.predHistory[0].t < now - PRED_HISTORY_MS) {
      this.predHistory.shift();
    }

    const decay = Math.exp((-dt * 1000) / RENDER_SMOOTH_MS);
    this.renderOffX *= decay;
    this.renderOffY *= decay;
    this.me.setPosition(this.predX + this.renderOffX, this.predY + this.renderOffY);
  }


  private sendInput(dx: number, dy: number, force: boolean): void {
    const now = this.time.now;
    const idle = now - this.lastInputSentAt;

    const changed =
      Math.abs(dx - this.lastDX) > INPUT_EPSILON || Math.abs(dy - this.lastDY) > INPUT_EPSILON;
    if (!force) {
      if (idle < INPUT_MIN_INTERVAL_MS) return; // teto de mensagens por segundo
      if (!changed && idle < INPUT_RESEND_MS) return;
    }

    this.lastDX = dx;
    this.lastDY = dy;
    this.lastInputSentAt = now;
    this.socket.sendInput(dx, dy, ++this.inputSeq);
    if (changed || force) this.motionSeq = this.inputSeq;
  }

  /**
   * Reconciliacao: o servidor e a autoridade, mas o snapshot chega atrasado.
   * Compara-lo com a predicao ATUAL puxava o avatar para tras enquanto ele
   * andava, e ao parar ele escorregava ate o servidor. Por isso, andando,
   * comparamos com onde a predicao estava ha uma ida e volta (mais meio tick);
   * parado e com o ultimo movimento confirmado (ack), a posicao do servidor e
   * final e vale integralmente. A correcao entra na predicao na hora, mas o
   * desenho a alcanca suavemente (renderOff).
   */
  private reconcile(serverX: number, serverY: number, ack: number): void {
    if (Math.hypot(serverX - this.predX, serverY - this.predY) > SNAP_DISTANCE) {
      // Colisao que o cliente nao previu, teleporte: salto imediato.
      this.predX = serverX;
      this.predY = serverY;
      this.resetPrediction();
      return;
    }

    const walking = this.lastDX !== 0 || this.lastDY !== 0;
    const settled = !walking && ack >= this.motionSeq;
    const [refX, refY] = settled
      ? [this.predX, this.predY]
      : this.predictionAt(this.time.now - this.socket.latency - this.tickMs / 2);

    const gain = settled ? 1 : WALK_CORRECTION;
    const ex = (serverX - refX) * gain;
    const ey = (serverY - refY) * gain;
    if (Math.abs(ex) < 0.01 && Math.abs(ey) < 0.01) return;

    this.predX += ex;
    this.predY += ey;
    this.renderOffX -= ex;
    this.renderOffY -= ey;
    // O historico passa a refletir a predicao corrigida, senao o mesmo erro
    // seria corrigido de novo no proximo snapshot.
    for (const h of this.predHistory) {
      h.x += ex;
      h.y += ey;
    }
  }

  /** Posicao prevista no instante t, interpolada a partir do historico. */
  private predictionAt(t: number): [number, number] {
    const hist = this.predHistory;
    if (hist.length === 0 || t >= hist[hist.length - 1].t) return [this.predX, this.predY];
    if (t <= hist[0].t) return [hist[0].x, hist[0].y];

    let i = hist.length - 1;
    while (hist[i - 1].t > t) i--;
    const a = hist[i - 1];
    const b = hist[i];
    const k = b.t === a.t ? 1 : (t - a.t) / (b.t - a.t);
    return [a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k];
  }

  private setMySeat(seat: number): void {
    this.mySeat = seat;
    if (this.me) this.me.seat = seat;
    // sentar encerra os gestos com pose (os de emoji continuam)
    if (seat && this.me?.gesture && !gestureById(this.me.gesture)?.emote) this.setMyGesture(undefined);
  }

  /**
   * Escolheu um gesto no menu ou no atalho. O gesto aparece na hora (como a
   * predicao do movimento) e o servidor repassa para os outros no snapshot.
   * Escolher o gesto em andamento para.
   */
  private selectGesture(id: string): void {
    if (this.overlayOpen || this.ctx.hud.chatFocused || !this.me) return;
    const def = gestureById(id);
    if (!def) return;
    if (this.me.gesture === id) {
      this.setMyGesture(undefined);
      this.socket.sendGesture("");
      return;
    }
    const standing = this.mySeat !== 0 && !def.emote;
    if (!def.emote) this.me.setFacing(0, false);
    this.setMyGesture(id);
    this.socket.sendGesture(id);
    if (standing) {
      // gesto com pose e de pe: sentado, levanta (o servidor faz o mesmo). O
      // input forcado depois do gesto segura o assento previsto ate o servidor
      // confirmar, senao um snapshot atrasado sentaria o avatar de novo.
      this.standUpLocally();
      this.sendInput(0, 0, true);
    }
  }

  private setMyGesture(id: string | undefined): void {
    this.me?.setGesture(id);
    this.ctx.hud.gestures.setActive(id);
  }

  private standUpLocally(): void {
    const seat = this.interactables.find((it) => it.id === this.mySeat);
    this.setMySeat(0);
    if (seat?.standX === undefined || seat.standY === undefined) return; // o servidor corrige
    this.predX = seat.standX;
    this.predY = seat.standY;
    this.resetPrediction();
  }

  private resetPrediction(): void {
    this.predHistory = [];
    this.renderOffX = 0;
    this.renderOffY = 0;
  }

  // ------------------------------------------------------------- colisao

  private solidAt(tileX: number, tileY: number): boolean {
    if (tileX < 0 || tileY < 0 || tileX >= this.mapWidth || tileY >= this.mapHeight) return true;
    if (this.collision.length === 0) return false;
    return this.collision[tileY * this.mapWidth + tileX] !== 0;
  }

  private canStand(x: number, y: number): boolean {
    const top = y + BODY_OFFSET - BODY_HALF_H;
    const bottom = y + BODY_OFFSET + BODY_HALF_H;
    const left = x - BODY_HALF_W;
    const right = x + BODY_HALF_W;

    const w = this.mapWidth * this.tileSize;
    const h = this.mapHeight * this.tileSize;
    if (left < 0 || top < 0 || right >= w || bottom >= h) return false;

    const x0 = Math.floor(left / this.tileSize);
    const x1 = Math.floor((right - 0.001) / this.tileSize);
    const y0 = Math.floor(top / this.tileSize);
    const y1 = Math.floor((bottom - 0.001) / this.tileSize);

    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (this.solidAt(tx, ty)) return false;
      }
    }
    return true;
  }

  /** Move eixo a eixo, permitindo deslizar pelas paredes (igual ao servidor). */
  private slide(x: number, y: number, dx: number, dy: number): [number, number] {
    if (dx !== 0 && this.canStand(x + dx, y)) x += dx;
    if (dy !== 0 && this.canStand(x, y + dy)) y += dy;
    return [x, y];
  }

  // --------------------------------------------------------- interacao

  private updateInteractionHint(): void {
    if (this.overlayOpen) {
      this.ctx.hud.setHint(null);
      this.ctx.touch.setInteract(null);
      return;
    }

    if (this.mySeat !== 0) {
      // sentado, a acao e sempre levantar (andar tambem levanta)
      this.nearby = null;
      this.ctx.hud.setHint("<b>E</b> — Levantar");
      this.ctx.touch.setInteract("Levantar");
      return;
    }

    // A Foxy passa na frente de tudo (igual ao servidor): perto dela, a acao e
    // o carinho. A distancia usa a posicao do snapshot, nao a interpolada, para
    // a dica bater com o que o servidor vai decidir.
    const foxy = this.foxy?.serverPosition;
    this.nearFoxy =
      !!foxy && Math.hypot(foxy.x - this.predX, foxy.y - this.predY) <= this.foxyRadius;
    if (this.nearFoxy) {
      this.nearby = null;
      this.ctx.hud.setHint(`<b>E</b> — ${escapeHtml(this.foxyLabel)}`);
      this.ctx.touch.setInteract(this.foxyLabel);
      return;
    }

    let closest: Interactable | null = null;
    let closestDistance = Infinity;
    const taken = this.takenSeats();

    for (const item of this.interactables) {
      if (item.kind === KIND_SEAT && taken.has(item.id)) continue; // o servidor senta no livre
      const distance = Math.hypot(item.x - this.predX, item.y - this.predY);
      if (distance <= item.radius && distance < closestDistance) {
        closest = item;
        closestDistance = distance;
      }
    }

    this.nearby = closest;
    const label = closest ? closest.label || closest.name : null;

    // No teclado a dica fica no rodape; no toque ela vira o proprio botao.
    this.ctx.hud.setHint(label ? `<b>E</b> — ${escapeHtml(label)}` : null);
    this.ctx.touch.setInteract(label);
  }

  /** Assentos ocupados por outros jogadores, segundo o ultimo snapshot. */
  private takenSeats(): Set<number> {
    const taken = new Set<number>();
    for (const avatar of this.avatars.values()) if (avatar.seat) taken.add(avatar.seat);
    return taken;
  }

  private tryInteract(): void {
    if (this.overlayOpen || this.ctx.hud.chatFocused) return;
    this.ctx.hud.gestures.close();
    if (!this.nearby && !this.nearFoxy && this.mySeat === 0) return;
    this.socket.sendInteract();
  }

  private openMinigame(game: string, label: string): void {
    if (this.overlayOpen) return;

    const sceneKey = MINIGAME_SCENES[game];
    if (!sceneKey) {
      // Mapa com um jogo que este cliente ainda nao conhece (versao antiga em
      // cache, por exemplo): melhor avisar do que abrir uma cena vazia.
      console.warn("minigame sem cena registrada", game);
      this.ctx.hud.addSystem(`O minigame "${label}" não está disponível nesta versão.`, "warn");
      return;
    }
    this.overlayOpen = true;
    this.dm?.setSuspended(true); // a barra ficaria por cima do minigame
    this.sendInput(0, 0, true); // o avatar para enquanto o jogador esta no menu/jogo
    this.ctx.hud.setHint(null);

    void this.runMinigameMenu(game, sceneKey, label);
  }

  /**
   * Tela de entrada de cada minigame: jogar ou ver os recordes daquele jogo.
   * Fica em loop ate o jogador escolher voltar para o Jardim, entao dar uma
   * partida ou consultar o placar sempre retorna para este menu.
   */
  private async runMinigameMenu(game: string, sceneKey: string, label: string): Promise<void> {
    const { session, touch } = this.ctx;

    for (;;) {
      touch.setMode("hidden");
      const choice = await showMinigameMenu({ title: label });

      if (choice === "records") {
        await showLeaderboard({
          slug: game,
          title: label,
          token: session.token,
          currentUserId: session.user.id,
        });
        continue;
      }

      if (choice === "play") {
        await this.playMinigame(game, sceneKey, label);
        continue;
      }

      break;
    }

    this.overlayOpen = false;
    this.dm?.setSuspended(false);
    touch.setMode("world");
  }

  private playMinigame(game: string, sceneKey: string, label: string): Promise<void> {
    return new Promise((resolve) => {
      // O HUD sai de cena: no celular o campo de chat fica exatamente onde o
      // jogador toca para bater asa.
      this.ctx.hud.hide();
      this.ctx.touch.setMode("minigame");

      this.scene.launch(sceneKey, {
        game,
        label,
        session: this.ctx.session,
        touch: this.ctx.touch,
        onExit: () => {
          this.ctx.hud.show();
          this.scene.resume("garden");
          resolve();
        },
      });
      this.scene.pause("garden");
    });
  }

  /** Placa do jardim: so mostra o texto. */
  private openInfo(title: string, text: string): void {
    if (this.overlayOpen) return;
    this.overlayOpen = true;

    this.sendInput(0, 0, true);
    this.ctx.touch.setMode("hidden");
    void showInfo(title, text).then(() => {
      this.overlayOpen = false;
      this.ctx.touch.setMode("world");
    });
  }

  /** `allGames`: a placa do upRanking, com uma aba por minigame. */
  private openLeaderboard(game: string, label: string, allGames = false): void {
    if (this.overlayOpen) return;
    this.overlayOpen = true;

    this.sendInput(0, 0, true);
    this.ctx.touch.setMode("hidden");
    const session = this.ctx.session;

    void showLeaderboard({
      slug: game,
      title: label,
      tabs: allGames ? RANKING_TABS : undefined,
      token: session.token,
      currentUserId: session.user.id,
    }).then(() => {
      this.overlayOpen = false;
      this.ctx.touch.setMode("world");
    });
  }

  private openPlayers(): void {
    if (this.overlayOpen) return;
    this.overlayOpen = true;

    this.sendInput(0, 0, true);
    this.ctx.touch.setMode("hidden");
    // mandar mensagem ou editar o proprio dossie fecham a lista antes
    let editAfter = false;
    const onDossier = async (id: string) => {
      const result = await this.viewDossier(id);
      editAfter = result === "edit";
      return result !== null;
    };
    void showPlayersPanel(this.ctx.session, onDossier).then(() => {
      this.overlayOpen = false;
      this.ctx.touch.setMode("world");
      if (editAfter) this.openProfile();
    });
  }

  /**
   * Meu Dossie. `session` e `pendingReconnect` vem de uma abertura anterior
   * (editar a partir do "Ver como os outros veem"): o nome pode ter mudado e
   * o mundo ainda precisa reconectar.
   */
  private openProfile(session?: Session, pendingReconnect = false): void {
    if (this.overlayOpen) return;
    this.overlayOpen = true;

    this.sendInput(0, 0, true);
    this.ctx.touch.setMode("hidden");
    session ??= this.ctx.session;

    void showProfileMenu(session, (user) => this.ctx.hud.setProfile(user)).then((result) => {
      this.overlayOpen = false;
      const changed = result.changed || pendingReconnect;
      if (result.leave) {
        this.ctx.onLeave();
        return;
      }
      if (result.customize) {
        this.openCustomizer(result.session, false, changed);
        return;
      }
      if (result.preview) {
        // o nome novo so vale no mundo depois de reconectar: espera a ficha fechar
        void this.viewDossier(result.session.user.id).then((next) => {
          if (next === "edit") this.openProfile(result.session, changed);
          else if (changed) this.ctx.onProfileUpdated(result.session);
        });
        return;
      }
      this.ctx.touch.setMode("world");
      if (changed) this.ctx.onProfileUpdated(result.session);
    });
  }

  /** Avatar sob o ponto da tela, ou null. */
  private avatarAt(screenX: number, screenY: number): Avatar | null {
    const world = this.cameras.main.getWorldPoint(screenX, screenY);
    let found: Avatar | null = null;
    // o de baixo na tela fica na frente (y-sorting): ganha o de maior y
    for (const avatar of [...this.avatars.values(), ...(this.me ? [this.me] : [])]) {
      if (!avatar.hit(world.x, world.y)) continue;
      if (!found || avatar.anchor.y > found.anchor.y) found = avatar;
    }
    return found;
  }

  private dossierAt(screenX: number, screenY: number): void {
    if (this.overlayOpen) return;
    const avatar = this.avatarAt(screenX, screenY);
    if (avatar) this.openDossier(avatar.id);
  }

  /** Abre o dossie fora de outro overlay (mapa, chat, mensagens). */
  private openDossier(userId: string): void {
    void this.viewDossier(userId).then((result) => {
      if (result === "edit") this.openProfile();
    });
  }

  /**
   * Abre o dossie de alguem. Pode vir de dentro de outro overlay (lista de
   * jogadores): a ficha abre por cima e o estado do overlay de baixo fica como
   * estava. Mandar mensagem ja abre a conversa; editar fica com quem chamou
   * (o Meu Dossie so abre depois que o overlay de baixo fechar).
   */
  private async viewDossier(userId: string): Promise<DossierResult> {
    const session = this.ctx.session;
    const ownsOverlay = !this.overlayOpen;
    if (ownsOverlay) {
      this.overlayOpen = true;
      this.sendInput(0, 0, true);
      this.ctx.touch.setMode("hidden");
    }
    const result = await showDossier({ token: session.token, userId, viewerId: session.user.id, canMessage: !!this.dm });
    if (ownsOverlay) {
      this.overlayOpen = false;
      this.ctx.touch.setMode("world");
    }
    if (result === "message") void this.dm?.openDirectWith(userId);
    return result;
  }

  /**
   * Customizador de avatar. Salvar reconecta ao mundo (como trocar o nome)
   * para todo mundo ver o avatar novo. `pendingReconnect` cobre o caso de o
   * nome ter mudado no menu de perfil antes de abrir o customizador.
   */
  private openCustomizer(session: Session, firstTime: boolean, pendingReconnect = false): void {
    if (this.overlayOpen) return;

    // Sem as partes (algum PNG falhou) nao ha como desenhar o editor: segue
    // com o preset. Na primeira entrada, em silencio; pedido pelo perfil, avisa.
    if (!avatarPartsReady()) {
      if (!firstTime) this.ctx.hud.addSystem("O editor de avatar não carregou. Recarregue a página e tente de novo.", "warn");
      if (pendingReconnect) this.ctx.onProfileUpdated(session);
      return;
    }

    this.overlayOpen = true;
    this.sendInput(0, 0, true);
    this.ctx.touch.setMode("hidden");

    let current = session;
    const done = (saved: boolean) => {
      this.overlayOpen = false;
      this.ctx.touch.setMode("world");
      if (saved || pendingReconnect) this.ctx.onProfileUpdated(current);
    };
    void showAvatarCustomizer({
      initial: session.user.appearance ?? presetFor(session.user.skin),
      firstTime,
      save: async (appearance) => {
        const result = await api.updateAppearance(session.token, appearance);
        current = { ...session, user: result.user };
        this.ctx.hud.setProfile(result.user);
      },
    }).then(
      (saved) => done(saved !== null),
      (error) => {
        // Nunca deixa o jogador preso atras de um overlay quebrado.
        console.error("customizador de avatar falhou", error);
        this.ctx.hud.addSystem("O editor de avatar falhou. Seu avatar continua o mesmo.", "warn");
        done(false);
      },
    );
  }

  // -------------------------------------------------------- chat de voz

  /**
   * Liga o chat de voz se o servidor tem LiveKit. O servidor manda a sala
   * (mensagem "voice") na entrada e a cada troca; aqui so seguimos.
   */
  private setupVoice(): void {
    const { welcome, hud } = this.ctx;
    const panel = hud.voice;
    if (!welcome.voice) {
      panel.setAvailable(false);
      return;
    }
    this.voiceInfo = welcome.voice;
    this.drawVoiceZones(welcome.voice);

    this.voice = new VoiceChat({
      info: welcome.voice,
      requestToken: () => this.socket.requestVoice(),
      onState: (state) => {
        this.voiceKind = state.kind;
        panel.setState(state);
        // ao ligar o microfone na area aberta pela primeira vez, avisa no chat
        if (state.mic && state.kind === "open" && !this.openWarned) {
          this.openWarned = true;
          hud.addSystem(OPEN_NOT_PRIVATE, "warn");
        }
      },
      onSpeaking: (ids) => {
        this.me?.setSpeaking(ids.has(this.myId));
        for (const [id, avatar] of this.avatars) avatar.setSpeaking(ids.has(id));
      },
      onNotice: (text, level) => {
        hud.addSystem(text, level);
        if (level !== "info") hud.showBanner(text, level);
      },
    });
    panel.setAvailable(true);
    panel.onMic(() => this.voice?.toggleMic());
    panel.onAction((action) => {
      if (action.kind === "lock") this.socket.lockVoice();
      else if (action.kind === "unlock") this.socket.unlockVoice();
      else this.socket.knockVoice(action.locked);
    });
    panel.onAdmit((id) => this.socket.admitVoice(id));

    this.unsubscribes.push(
      this.socket.on("voice", (msg) => void this.voice?.join(msg)),
      this.socket.on("voice_knock", (msg) => panel.addKnock(msg.id, msg.name)),
    );
  }

  /** Volume pela distancia e o botao de trancar/pedir para entrar. */
  private updateVoice(): void {
    if (!this.voice || !this.voiceInfo || !this.me) return;
    const now = this.time.now;
    if (now - this.voiceUpdatedAt < VOICE_PROXIMITY_MS) return;
    this.voiceUpdatedAt = now;

    const peers = new Map<string, VoicePeer>();
    for (const [id, avatar] of this.avatars) {
      peers.set(id, { x: avatar.anchor.x, y: avatar.anchor.y, locked: !!avatar.lockedId });
    }
    const me = { x: this.predX, y: this.predY };
    this.voice.updateProximity(me, peers);
    this.ctx.hud.voice.setAction(this.voiceAction(me));
  }

  /**
   * O que o botao ao lado do status faz agora. E so a dica: quem decide e o
   * servidor (que recusa com um aviso se nao der).
   */
  private voiceAction(me: { x: number; y: number }): VoiceAction {
    if (this.myLocked) return { kind: "unlock" };
    if (this.voiceKind !== "open" || !this.voiceInfo) return null;
    const radius = this.voiceInfo.hearRadius;
    let knock: string | null = null;
    for (const avatar of this.avatars.values()) {
      if (Math.hypot(avatar.anchor.x - me.x, avatar.anchor.y - me.y) > radius) continue;
      if (!avatar.lockedId) return { kind: "lock" };
      knock ??= avatar.lockedId;
    }
    return knock ? { kind: "knock", locked: knock } : null;
  }

  /** Salas de voz no chao: um contorno suave e o nome no alto de cada uma. */
  private drawVoiceZones(info: VoiceInfo): void {
    const g = this.add.graphics().setDepth(DEPTH.decor + 0.5);
    for (const z of info.zones) {
      g.fillStyle(0xffc46b, 0.07);
      g.lineStyle(1, 0xffc46b, 0.4);
      if (z.ellipse) {
        g.fillEllipse(z.x + z.w / 2, z.y + z.h / 2, z.w, z.h);
        g.strokeEllipse(z.x + z.w / 2, z.y + z.h / 2, z.w, z.h);
      } else {
        g.fillRect(z.x, z.y, z.w, z.h);
        g.strokeRect(z.x, z.y, z.w, z.h);
      }
      const label = this.add
        .text(z.x + z.w / 2, z.y + 3, `🔊 ${z.label}`, {
          fontFamily: FONT,
          fontSize: "9px",
          fontStyle: "bold",
          color: "#ffc46b",
          resolution: textResolution(this.zoom),
        })
        .setOrigin(0.5, 0)
        .setAlpha(0.85)
        .setDepth(DEPTH.decor + 0.6);
      this.voiceZoneLabels.push(label);
    }
    this.voiceZoneLayer = g;
  }

  private teardown(): void {
    this.dm?.destroy();
    this.dm = null;
    this.ctx.hud.setDmAvailable(false);
    this.voice?.destroy();
    this.voice = null;
    this.ctx.hud.voice.setAvailable(false);
    this.voiceZoneLayer?.destroy();
    this.voiceZoneLayer = null;
    for (const label of this.voiceZoneLabels) label.destroy();
    this.voiceZoneLabels = [];
    for (const off of this.unsubscribes) off();
    this.unsubscribes = [];
    for (const avatar of this.avatars.values()) avatar.destroy();
    this.avatars.clear();
    this.foxy?.destroy();
    this.foxy = null;
    this.me?.destroy();
    this.me = null;
    this.ctx.touch.setMode("hidden");
  }
}

function directionOf(dx: number, dy: number): number {
  if (Math.abs(dx) > Math.abs(dy)) return dx > 0 ? 2 : 1; // right : left
  return dy > 0 ? 0 : 3; // down : up
}

/** Mantem a ultima direcao ao parar, em vez de sempre virar para baixo. */
function currentDirection(avatar: Avatar): number {
  const key = avatar.sprite.anims.currentAnim?.key ?? "";
  if (key.endsWith("-left")) return 1;
  if (key.endsWith("-right")) return 2;
  if (key.endsWith("-up")) return 3;
  return 0;
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
